import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

export const modalPanelClass =
  "relative w-full max-h-[min(92dvh,920px)] flex flex-col overflow-hidden " +
  "rounded-t-2xl sm:rounded-2xl shadow-2xl " +
  "border border-gray-200/80 dark:border-gray-700 " +
  "bg-white dark:bg-gray-800 " +
  "pb-[env(safe-area-inset-bottom)]";

interface AnimatedModalProps {
  isOpen?: boolean;
  onClose: () => void;
  children: React.ReactNode;
  backdropClassName?: string;
  /** Extra classes on the dialog wrapper (e.g. max-w-lg). */
  contentClassName?: string;
  /** Prevent closing via backdrop / Escape. */
  disableClose?: boolean;
}

/**
 * Shared modal shell: portal, scroll lock, Escape to close,
 * bottom-sheet on mobile, centered card on desktop.
 */
export const AnimatedModal: React.FC<AnimatedModalProps> = ({
  isOpen = true,
  onClose,
  children,
  backdropClassName = "bg-black/60 backdrop-blur-sm",
  contentClassName = "sm:max-w-lg",
  disableClose = false,
}) => {
  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || disableClose) return;
      // Don't close the modal while a portaled select menu is open
      if (document.querySelector('[role="listbox"]')) return;
      onClose();
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose, disableClose]);

  const portalTarget =
    typeof document !== "undefined"
      ? document.getElementById("modal-root") || document.body
      : null;

  if (!portalTarget) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          role="presentation"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={() => {
            if (!disableClose) onClose();
          }}
          className={`fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 ${backdropClassName}`}
        >
          {/* Mobile drag affordance spacer — keeps sheet from feeling stuck to edges */}
          {/*
            Avoid animating transform/scale here — a transformed ancestor
            breaks native date/time pickers and fixed portals in Safari/Chrome.
          */}
          <motion.div
            role="dialog"
            aria-modal="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={(e) => e.stopPropagation()}
            className={`w-full mx-auto sm:animate-none ${contentClassName} max-sm:translate-y-0`}
          >
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    portalTarget
  );
};

export default AnimatedModal;
